"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { FEATURES } from '@/app/lib/config';

interface DemoContextType {
  /**
   * Whether the current view is in demo mode (viewing a demo work)
   */
  isDemo: boolean;
  
  /**
   * The ID of the demo work being viewed, if any
   */
  demoWorkId: string | null;
  
  /**
   * Set the demo mode explicitly (usually done when loading a demo work)
   */
  setDemoMode: (isDemoMode: boolean, workId?: string | null) => void;
}

const DemoContext = createContext<DemoContextType>({
  isDemo: false,
  demoWorkId: null,
  setDemoMode: () => {}
});

/**
 * Hook to access the demo context
 */
export const useDemoMode = () => useContext(DemoContext);

interface DemoProviderProps {
  children: ReactNode;
}

/**
 * Provider component for demo mode state
 */
export const DemoProvider = ({ children }: DemoProviderProps) => {
  const [isDemo, setIsDemo] = useState(false);
  const [demoWorkId, setDemoWorkId] = useState<string | null>(null);
  const pathname = usePathname();

  // Reset demo mode when navigating away from a book path
  useEffect(() => {
    // Only run this if the feature is enabled
    if (!FEATURES.PUBLIC_DEMO_WORKS) return;

    // Match book paths like /book/{friendlyId}
    const bookPathMatch = pathname?.match(/\/book\/([^\/]+)/);
    
    if (!bookPathMatch) {
      // We're not on a book path, reset demo mode if it was set
      if (isDemo) {
        setIsDemo(false);
        setDemoWorkId(null);
      }
    } else if (demoWorkId && bookPathMatch[1] !== demoWorkId) {
      // We've navigated to a different book, reset demo mode
      // The book page itself will set demo mode if needed
      setIsDemo(false);
      setDemoWorkId(null);
    }
  }, [pathname, isDemo, demoWorkId]);

  const setDemoMode = (isDemoMode: boolean, workId: string | null = null) => {
    setIsDemo(isDemoMode);
    setDemoWorkId(workId);
  };

  // Value object for the context provider
  const value = {
    isDemo,
    demoWorkId,
    setDemoMode
  };

  return (
    <DemoContext.Provider value={value}>
      {children}
    </DemoContext.Provider>
  );
};

export default DemoContext;
